import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Play, Pause, SkipForward, SkipBack, Volume2, Search, Music } from 'lucide-react'
import { spotifyAPI } from '../../services/api'
import toast from 'react-hot-toast'

interface SpotifyWidgetProps {
  widget: any
  isExpanded: boolean
}

interface Track {
  id: string
  name: string
  artist: string
  album: string
  albumArt: string
  duration: number
  uri: string
}

export default function SpotifyWidget({ widget, isExpanded }: SpotifyWidgetProps) {
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTrack, setCurrentTrack] = useState<Track | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<Track[]>([])
  const [recentTracks, setRecentTracks] = useState<Track[]>([])
  const [currentTime, setCurrentTime] = useState(0)
  const [volume, setVolume] = useState(80)

  // Check Spotify connection status
  const { data: statusData, isLoading: statusLoading } = useQuery({
    queryKey: ['spotify-status'],
    queryFn: async () => {
      const { data } = await spotifyAPI.getStatus()
      return data
    },
  })

  // Mock data - Replace with actual Spotify API integration
  useEffect(() => {
    const mockTrack: Track = {
      id: '1',
      name: 'Blinding Lights',
      artist: 'The Weeknd',
      album: 'After Hours',
      albumArt: 'https://i.scdn.co/image/ab67616d0000b2738863bc11d2aa12b54f5aeb36',
      duration: 200,
      uri: 'spotify:track:0VjIjW4GlUZAMYd2vXMi3b'
    }

    const mockRecent: Track[] = [
      mockTrack,
      {
        id: '2',
        name: 'Save Your Tears',
        artist: 'The Weeknd',
        album: 'After Hours',
        albumArt: 'https://i.scdn.co/image/ab67616d0000b2738863bc11d2aa12b54f5aeb36',
        duration: 215,
        uri: 'spotify:track:5QO79kh1waicV47BqGRL3g'
      },
      {
        id: '3',
        name: 'Starboy',
        artist: 'The Weeknd ft. Daft Punk',
        album: 'Starboy',
        albumArt: 'https://i.scdn.co/image/ab67616d0000b2734718e2b124f79258be7bc452',
        duration: 230,
        uri: 'spotify:track:7MXVkk9YMctZqd1Srtv4MB'
      }
    ]

    setRecentTracks(mockRecent)
    setCurrentTrack(mockTrack)
  }, [])

  // Simulate playback progress
  useEffect(() => {
    if (isPlaying && currentTrack) {
      const interval = setInterval(() => {
        setCurrentTime(prev => {
          if (prev >= currentTrack.duration) {
            setIsPlaying(false)
            return 0
          }
          return prev + 1
        })
      }, 1000)

      return () => clearInterval(interval)
    }
  }, [isPlaying, currentTrack])

  const handlePlayPause = () => {
    setIsPlaying(!isPlaying)
    // TODO: Integrate with Spotify Web Playback SDK
  }

  const handleSearch = async (query: string) => {
    setSearchQuery(query)
    if (query.length < 2) {
      setSearchResults([])
      return
    }

    // TODO: Call Spotify Search API
    // Mock search results
    const mockResults: Track[] = [
      {
        id: '4',
        name: query,
        artist: 'Search Result Artist',
        album: 'Search Album',
        albumArt: 'https://via.placeholder.com/300',
        duration: 180,
        uri: 'spotify:track:mock'
      }
    ]
    setSearchResults(mockResults)
  }

  const handleTrackSelect = (track: Track) => {
    setCurrentTrack(track)
    setCurrentTime(0)
    setIsPlaying(true)
    setSearchQuery('')
    setSearchResults([])
  }

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const connectToSpotify = async () => {
    try {
      const { data } = await spotifyAPI.getAuthUrl()
      if (data.auth_url) {
        // Open Spotify OAuth in popup
        const width = 600
        const height = 700
        const left = window.screen.width / 2 - width / 2
        const top = window.screen.height / 2 - height / 2
        
        window.open(
          data.auth_url,
          'Spotify Login',
          `width=${width},height=${height},left=${left},top=${top}`
        )
      } else if (data.error) {
        toast.error(data.error)
      }
    } catch (error: any) {
      console.error('Spotify OAuth error:', error)
      toast.error(error.response?.data?.error || 'Failed to connect to Spotify')
    }
  }

  if (statusLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-6">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mb-4"></div>
        <p className="text-gray-600">Loading...</p>
      </div>
    )
  }

  if (!statusData?.connected) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-6">
        <Music size={64} className="text-green-500 mb-4" />
        <h3 className="text-xl font-bold mb-2" style={{ color: 'var(--color-text-primary)' }}>Connect to Spotify</h3>
        <p className="mb-6" style={{ color: 'var(--color-text-secondary)' }}>
          Connect your Spotify account to start streaming music
        </p>
        <button
          onClick={connectToSpotify}
          className="px-6 py-3 bg-green-500 text-white rounded-full font-semibold hover:bg-green-600 transition flex items-center gap-2"
        >
          <Music size={20} />
          Connect Spotify
        </button>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col">
      {/* Search Bar */}
      {isExpanded && (
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="Search songs, artists, albums..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
          </div>

          {/* Search Results */}
          {searchResults.length > 0 && (
            <div className="mt-2 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
              {searchResults.map((track) => (
                <button
                  key={track.id}
                  onClick={() => handleTrackSelect(track)}
                  className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 transition"
                >
                  <img src={track.albumArt} alt="" className="w-12 h-12 rounded" />
                  <div className="flex-1 text-left">
                    <div className="font-medium text-gray-900">{track.name}</div>
                    <div className="text-sm text-gray-600">{track.artist}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Current Track */}
      {currentTrack && (
        <div className="mb-4">
          <div className="flex items-center gap-4">
            <img
              src={currentTrack.albumArt}
              alt={currentTrack.album}
              className={`rounded-lg shadow-lg ${isExpanded ? 'w-32 h-32' : 'w-16 h-16'}`}
            />
            <div className="flex-1">
              <h3 className={`font-bold text-gray-900 ${isExpanded ? 'text-xl' : 'text-base'}`}>
                {currentTrack.name}
              </h3>
              <p className={`text-gray-600 ${isExpanded ? 'text-base' : 'text-sm'}`}>
                {currentTrack.artist}
              </p>
              {isExpanded && (
                <p className="text-sm text-gray-500 mt-1">{currentTrack.album}</p>
              )}
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mt-4">
            <div className="flex items-center gap-2 text-xs text-gray-600 mb-1">
              <span>{formatTime(currentTime)}</span>
              <div className="flex-1 bg-gray-200 rounded-full h-1 overflow-hidden">
                <div
                  className="bg-green-500 h-full transition-all duration-1000"
                  style={{ width: `${(currentTime / currentTrack.duration) * 100}%` }}
                />
              </div>
              <span>{formatTime(currentTrack.duration)}</span>
            </div>
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="flex items-center justify-center gap-4 mb-4">
        <button
          onClick={() => {}}
          className="p-2 hover:bg-gray-100 rounded-full transition"
        >
          <SkipBack size={24} />
        </button>

        <button
          onClick={handlePlayPause}
          className="p-4 bg-green-500 text-white rounded-full hover:bg-green-600 transition"
        >
          {isPlaying ? <Pause size={24} /> : <Play size={24} className="ml-1" />}
        </button>

        <button
          onClick={() => {}}
          className="p-2 hover:bg-gray-100 rounded-full transition"
        >
          <SkipForward size={24} />
        </button>
      </div>

      {/* Volume Control */}
      {isExpanded && (
        <div className="flex items-center gap-3 mb-4 px-4">
          <Volume2 size={20} className="text-gray-600" />
          <input
            type="range"
            min="0"
            max="100"
            value={volume}
            onChange={(e) => setVolume(parseInt(e.target.value))}
            className="flex-1 h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer"
          />
          <span className="text-sm text-gray-600 w-10">{volume}%</span>
        </div>
      )}

      {/* Recently Played */}
      {isExpanded && (
        <div className="flex-1 overflow-y-auto">
          <h4 className="font-semibold text-gray-900 mb-3">Recently Played</h4>
          <div className="space-y-2">
            {recentTracks.map((track) => (
              <button
                key={track.id}
                onClick={() => handleTrackSelect(track)}
                className={`w-full flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition ${
                  currentTrack?.id === track.id ? 'bg-green-50 border-2 border-green-500' : ''
                }`}
              >
                <img src={track.albumArt} alt="" className="w-12 h-12 rounded" />
                <div className="flex-1 text-left">
                  <div className="font-medium text-gray-900 text-sm">{track.name}</div>
                  <div className="text-xs text-gray-600">{track.artist}</div>
                </div>
                <span className="text-xs text-gray-500">{formatTime(track.duration)}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Compact View Queue */}
      {!isExpanded && recentTracks.length > 0 && (
        <div className="text-xs text-gray-500 text-center">
          {recentTracks.length} tracks in queue
        </div>
      )}
    </div>
  )
}

